import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeftRight, Camera } from "lucide-react";
import { PROJECTS } from "../data";

const ProjectShowcase = ({ project, index }) => {
  const hasBefore = project.before && project.before.length > 0;
  const initialView =
    typeof window !== "undefined" && new URLSearchParams(window.location.search).get("view") === "after"
      ? "after"
      : hasBefore
      ? "before"
      : "after";
  const [view, setView] = useState(initialView);
  const images = view === "after" ? project.after : project.before;

  return (
    <div
      data-testid={`project-showcase-${project.id}`}
      className="border-t border-white/10 pt-14 md:pt-20 first:border-t-0 first:pt-0"
    >
      <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end mb-10 md:mb-14">
        <div className="md:col-span-7">
          <div className="flex items-center gap-3 mb-5">
            <span className="font-mono-tech text-sm text-[var(--turq)]">
              {String(index + 1).padStart(2, "0")}
            </span>
            <span className="font-mono-tech text-[10px] uppercase tracking-[0.25em] text-white/40">
              {project.location} · {project.year}
            </span>
          </div>
          <h3 className="font-display text-3xl md:text-5xl font-medium tracking-tight leading-[1.02]">
            {project.title}
          </h3>
          <p className="mt-3 text-lg text-[var(--turq)]/90 font-display">{project.subtitle}</p>
          <p className="mt-5 text-white/60 leading-relaxed max-w-xl">{project.description}</p>
        </div>

        {/* Voor / Na toggle */}
        <div className="md:col-span-5 md:flex md:justify-end">
          <div className="inline-flex flex-col gap-3">
            <span className="font-mono-tech text-xs uppercase tracking-[0.2em] text-white/70 flex items-center gap-2">
              <ArrowLeftRight size={14} className="text-[var(--orange)]" /> Kies weergave — voor of na
            </span>
            <div
              className="inline-flex p-1.5 bg-white/10 rounded-full border border-white/20 shadow-lg"
              data-testid={`toggle-${project.id}`}
            >
              <button
                onClick={() => hasBefore && setView("before")}
                disabled={!hasBefore}
                data-testid={`toggle-before-${project.id}`}
                className={`relative px-8 py-3 text-base font-semibold rounded-full transition-colors duration-300 ${
                  view === "before" ? "text-[var(--ink)]" : "text-white/80"
                } ${!hasBefore ? "opacity-40 cursor-not-allowed" : "hover:text-white"}`}
              >
                {view === "before" && (
                  <motion.span layoutId={`pill-${project.id}`} className="absolute inset-0 bg-[var(--orange)] rounded-full" />
                )}
                <span className="relative">Voor</span>
              </button>
              <button
                onClick={() => setView("after")}
                data-testid={`toggle-after-${project.id}`}
                className={`relative px-8 py-3 text-base font-semibold rounded-full transition-colors duration-300 ${
                  view === "after" ? "text-[var(--ink)]" : "text-white/80 hover:text-white"
                }`}
              >
                {view === "after" && (
                  <motion.span layoutId={`pill-${project.id}`} className="absolute inset-0 bg-[var(--turq)] rounded-full" />
                )}
                <span className="relative">Na</span>
              </button>
            </div>
            {!hasBefore && (
              <span className="font-mono-tech text-[10px] text-white/35 mt-1">Voor-foto's binnenkort beschikbaar</span>
            )}
          </div>
        </div>
      </div>

      {/* Gallery */}
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 md:gap-6">
        <AnimatePresence mode="popLayout">
          {images.map((src, i) => (
            <motion.figure
              key={src}
              layout
              data-testid={`project-img-${project.id}-${i}`}
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ duration: 0.5, delay: i * 0.04, ease: [0.16, 1, 0.3, 1] }}
              className="relative mb-5 md:mb-6 break-inside-avoid overflow-hidden border border-white/10 group"
            >
              <span className="absolute top-3 left-3 z-10 inline-flex items-center gap-1.5 bg-[var(--ink)]/80 backdrop-blur text-white px-3 py-1 font-mono-tech text-[10px] uppercase tracking-[0.15em]">
                <Camera size={11} /> {view === "after" ? "Na" : "Voor"}
              </span>
              <img
                src={src}
                alt={`${project.title} — ${view}`}
                loading="lazy"
                className="w-full h-auto object-cover scale-100 group-hover:scale-[1.04] transition-transform duration-[900ms] ease-out"
              />
            </motion.figure>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export const FeaturedProject = () => {
  return (
    <section id="project" data-testid="featured-project-section" className="bg-[var(--ink)] text-white py-24 md:py-40">
      <div className="max-w-[1600px] mx-auto px-6 md:px-10">
        <div className="flex items-center gap-4 mb-6">
          <span className="h-px w-12 bg-[var(--orange)]" />
          <span className="font-mono-tech text-xs uppercase tracking-[0.25em] text-white/50">Uitgelicht project</span>
        </div>
        <h2 className="font-display text-4xl md:text-6xl font-medium tracking-tight leading-[0.98] max-w-3xl mb-16 md:mb-24">
          Het verschil,<br />van <span className="text-white/40">voor</span> tot <span className="text-[var(--turq)]">na</span>.
        </h2>

        <div className="space-y-16 md:space-y-24">
          {PROJECTS.map((p, i) => (
            <ProjectShowcase key={p.id} project={p} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
